/**
 * Editor: a score anyone can type into.
 *
 * This board owes nothing to the rest of the app. `@app/score-editor` is a general notation
 * editor that knows nothing about orchestration, the book or the verifier, and this page is
 * one host of it. What a host supplies is the three things the package deliberately does not
 * have: a notation engine, an audio context, and somewhere for the keystrokes to land.
 *
 * Its own job is to be understandable. Everything the editor can do is in the menu bar, the
 * palette says what the next note will be, the inspector says what is true where the caret
 * stands, and the status bar plays it. A keystroke, a menu item and a palette button all
 * reach the document through `edit`, so there is one path in and one place a refusal is
 * shown.
 *
 * A score can arrive with the navigation, which is how Arrange and the Lab board hand their result
 * over. It is read once, at mount: after that the editor owns the document.
 *
 * The document the editor holds is always the written one -- the score its players read. The
 * concert-pitch view derives the sounding score from it for as long as it is on the screen,
 * and that derived score is what is drawn, printed and exported while it is. Nothing is
 * edited into it: an edit still names a note by its id, and the ids are the same in both
 * readings.
 *
 * The board holds one engraved page at a time, turned by the pager in the status bar, which
 * stays on the screen however far the page is scrolled. The page turns itself to the bar the
 * music reaches, and to the bar the caret moves into, so typing past the end of a page goes
 * on on the next (`use-shown-page.ts`). Playing is followed as the Arrange panels follow it
 * (`use-playhead.ts`), with one difference: a click here is an edit first. It selects, and
 * only while the music plays does it also move the music there -- stopped, play already
 * starts from the selection.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useLocation } from "react-router-dom";
import {
  atConcertPitch,
  clipboard,
  crossStaff,
  engrave,
  extractPart,
  ossia,
  tab,
  voices,
} from "@app/score-editor";
import type { Clip, Command, Hit, NotationEngine, VoiceChange } from "@app/score-editor";
import { ScoreView, useEditor, useRendering } from "@app/score-editor/react";
import ScoreHelp from "../components/ScoreHelp";
import ScoreInspector from "../components/ScoreInspector";
import ScoreMenuBar from "../components/ScoreMenuBar";
import ScorePalette from "../components/ScorePalette";
import ScoreStatus from "../components/ScoreStatus";
import Board from "../components/Board";
import { actionFor, entryFor } from "../lib/score-keys";
import { menusFor } from "../lib/score-menus";
import { SHORTCUT_ACTIONS } from "../lib/score-actions";
import type { ScoreActions } from "../lib/score-actions";
import {
  SCORE_FILE_TYPES,
  exportMidi,
  exportMusicXml,
  readScoreFile,
  saveScore,
} from "../lib/score-files";
import { printScore } from "../lib/score-print";
import { handedOver, opening, startingScore } from "../lib/score-opening";
import { useScoreMidi } from "../lib/use-score-midi";
import { useScoreSound } from "../lib/use-score-sound";
import { useScoreWidth } from "../lib/use-score-width";
import { FILLED, useFullscreen } from "../lib/use-fullscreen";
import { useFollowPlayhead, usePlayhead } from "../lib/use-playhead";
import { barOf, useShownPage, useTopOnTurn } from "../lib/use-shown-page";
import { notationEngine } from "../lib/verovio";

export default function EditorPage(): JSX.Element {
  const given = handedOver(useLocation().state);
  const [opened] = useState(() => opening(given));
  const editor = useEditor(opened.score);
  const sound = useScoreSound(editor.score, editor.selection);
  // A MIDI keyboard writes through the same door a typed letter does, so nothing below here
  // knows which of them a note came from.
  const midi = useScoreMidi(editor.type, sound.wake);

  const [engine, setEngine] = useState<NotationEngine | null>(null);
  const [engineError, setEngineError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    notationEngine().then(
      (loaded) => {
        if (live) setEngine(loaded);
      },
      (reason: unknown) => {
        if (live) setEngineError(reason instanceof Error ? reason.message : String(reason));
      },
    );
    return () => {
      live = false;
    };
  }, []);

  const [clip, setClip] = useState<Clip | null>(null);
  const [said, setSaid] = useState<string | null>(opened.refused);
  const [help, setHelp] = useState(false);
  // ⌘K writes a chord symbol, and the box it is typed into is in the inspector. The page
  // counts the requests rather than raising a flag, so a second press with the box already
  // open is still a request and still takes the caret.
  const [chordAsk, setChordAsk] = useState(0);
  const [figureAsk, setFigureAsk] = useState(0);
  const picker = useRef<HTMLInputElement>(null);
  const board = useRef<HTMLDivElement>(null);
  const desk = useRef<HTMLDivElement>(null);
  const fullscreen = useFullscreen(desk);
  const playhead = usePlayhead(editor.score, sound.playback);
  const pick = useCallback(
    (hit: Hit | null, extending: boolean): void => {
      editor.pick(hit, extending);
      if (sound.playback.playing) playhead.jump(hit);
    },
    [editor, playhead, sound.playback.playing],
  );

  // Verovio reads `scale` as a percentage of its own natural size, so the readout is the
  // number itself rather than a ratio to some chosen default. The page it lays out is as wide
  // as the desk, measured, so zooming in reflows the music instead of cropping it.
  const [scale, setScale] = useState(38);
  const pageWidth = useScoreWidth(board, scale);

  // What is on the screen: the written score, or the concert-pitch reading of it. The flag
  // lives in the entry state because a typed letter has to mean the same thing the screen
  // shows -- a `C` typed into a clarinet part here is the C that sounds.
  const concert = editor.entry.concert;
  const shown = useMemo(
    () => (concert ? atConcertPitch(editor.score) : editor.score),
    [concert, editor.score],
  );

  // Two ways of looking at the same score. Writing music, the page is as wide as the desk
  // and the paper the score is set on does not come into it; checking how it will print,
  // the paper is exactly the one the Page menu says, blank right margin and all. The
  // second is off by default because an editor is for the first.
  const [paper, setPaper] = useState(false);
  const { rendering, error } = useRendering(
    engine,
    shown,
    paper ? { scale, adjustPageHeight: false } : { scale, pageWidth },
  );
  const sheet = useShownPage(rendering, [
    barOf(editor.score, playhead.at),
    editor.entry.caret?.measureId ?? null,
  ]);
  useTopOnTurn(board, sheet.page);
  useFollowPlayhead(board, sound.playback.playing);

  // The keyboard is the fastest way into this editor, so the board takes focus on arrival
  // rather than waiting for a click that a user who means to type a note should not need.
  useEffect(() => board.current?.focus(), []);

  // The one door onto the document. Every layer of the package answers with a command or
  // with a sentence saying why there is not one, and both end up here.
  const edit = useCallback(
    (built: Command | string, label?: string): void => {
      if (typeof built === "string") {
        setSaid(built);
        return;
      }
      setSaid(null);
      editor.run(built, label);
    },
    [editor],
  );

  const copy = useCallback((): void => {
    const made = clipboard.copy(editor.score, editor.selection);
    if (typeof made === "string") {
      setSaid(made);
      return;
    }
    setSaid(null);
    setClip(made);
  }, [editor.score, editor.selection]);

  /**
   * Run a change to the voices on a staff, and stand the caret in what it left.
   *
   * Two steps rather than one because they are two kinds of thing: the command goes through
   * the history like any edit, and where the caret then stands is entry state, which no
   * command carries. The order matters -- applying the edit re-places the caret from the
   * selection, and this puts it in the voice that was just made.
   */
  const changeVoices = (made: VoiceChange | string, label: string): void => {
    if (typeof made === "string") {
      setSaid(made);
      return;
    }
    setSaid(null);
    editor.run(made.command, label);
    editor.type({ kind: "place", caret: made.caret });
  };

  const openFile = async (file: File): Promise<void> => {
    const read = await readScoreFile(file);
    setSaid(read.note);
    if (read.score !== null) editor.open(read.score);
  };

  const actions: ScoreActions = {
    score: editor.score,
    selection: editor.selection,
    entry: editor.entry,
    caret: editor.entry.caret,
    canUndo: editor.canUndo,
    canRedo: editor.canRedo,
    undoLabel: editor.undoLabel,
    redoLabel: editor.redoLabel,
    hasClip: clip !== null,
    playing: sound.playback.playing,
    loading: sound.loading,
    metronome: sound.metronome,
    countIn: sound.countIn,
    marks: sound.marks,
    loop: sound.loop,
    keyboard: midi,
    scale,
    paper,
    fullscreen: fullscreen.on,
    concert,
    edit,
    type: editor.type,
    select: editor.select,
    undo: editor.undo,
    redo: editor.redo,
    copy,
    cut: () => {
      copy();
      edit(clipboard.remove(editor.score, editor.selection), "cut");
    },
    paste: () => {
      const caret = editor.entry.caret;
      if (clip === null) setSaid("nothing has been copied yet");
      else if (caret === null) setSaid("click where the music should go");
      else edit(clipboard.paste(editor.score, clip, caret), "paste");
    },
    newScore: () => {
      // Opening a new score throws away the history as well as the notes, so it is asked
      // about: there is no undo across an open.
      if (!window.confirm("Start a new score? What is written here will be lost.")) return;
      setSaid(null);
      editor.open(startingScore());
    },
    openFile: () => picker.current?.click(),
    save: () => saveScore(editor.score),
    // What is exported and what is printed is what is on the screen, so a concert-pitch
    // score goes out at concert pitch. The editor's own file is always the written one.
    exportXml: () => exportMusicXml(shown),
    exportMidi: () => exportMidi(editor.score),
    print: () => {
      void printScore(shown).then(setSaid);
    },
    savePart: (id) => {
      const part = extractPart(editor.score, id);
      if (typeof part === "string") {
        setSaid(part);
        return;
      }
      setSaid(null);
      saveScore(part);
    },
    nudge: (x, y) => edit(engrave.nudge(editor.score, editor.selection, x, y)),
    unnudge: () => edit(engrave.reset(editor.score, editor.selection)),
    crossStaff: (delta) => edit(crossStaff.send(editor.score, editor.selection, delta)),
    uncrossStaff: () => edit(crossStaff.putBack(editor.score, editor.selection)),
    playPause: sound.playPause,
    stop: sound.stop,
    toggleMetronome: sound.toggleMetronome,
    toggleCountIn: sound.toggleCountIn,
    toggleMarks: sound.toggleMarks,
    toggleLoop: sound.toggleLoop,
    zoom: (by) => setScale((current) => Math.min(Math.max(current + by, 14), 80)),
    togglePaper: () => setPaper((drawn) => !drawn),
    toggleFullscreen: fullscreen.toggle,
    toggleConcert: () => editor.type({ kind: "concert", on: !concert }),
    addVoice: () =>
      changeVoices(
        voices.addVoice(editor.score, editor.entry.caret, editor.selection),
        "add a voice",
      ),
    removeVoice: () =>
      changeVoices(
        voices.removeVoice(editor.score, editor.entry.caret, editor.selection),
        "take out a voice",
      ),
    addOssia: () =>
      changeVoices(ossia.add(editor.score, editor.entry.caret, editor.selection), "offer an ossia"),
    removeOssia: () =>
      changeVoices(
        ossia.remove(editor.score, editor.entry.caret, editor.selection),
        "take the ossia away",
      ),
    toggleHelp: () => setHelp((open) => !open),
    writeChord: () => setChordAsk((asked) => asked + 1),
    writeFigures: () => setFigureAsk((asked) => asked + 1),
  };

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const asked = actionFor(event);
      if (asked !== null) {
        event.preventDefault();
        SHORTCUT_ACTIONS[asked](actions);
        return;
      }
      // A digit is a fret rather than a note value where the caret is on a fretted staff,
      // so the board is told which staff it is typing into before the stroke is read.
      const fretting = tab.tuningOf(editor.score, editor.entry.caret?.partId ?? null) !== null;
      const input = entryFor(event, editor.entry, fretting);
      if (input === null) return;
      event.preventDefault();
      editor.type(input);
    },
    [actions, editor],
  );

  return (
    <Board
      kicker="Score editor"
      title={editor.score.meta.title}
      lede="Click a bar and type: A–G write notes, 1–6 pick the value. Everything else is in the menus, and ? lists the keys."
      measure="full"
    >
      {/* Full screen takes the menus, the palette and the status bar with the page: an editor
          on the whole screen is still an editor (View → Full screen, Esc to leave). */}
      <div
        ref={desk}
        className={`space-y-3 ${fullscreen.on ? `${FILLED} overflow-y-auto bg-paper p-4` : ""}`}
      >
        <ScoreMenuBar menus={menusFor(actions)} />

        {[said, editor.refusal, midi.refusal]
          .filter((note): note is string => note !== null)
          .map((note) => (
            <p
              key={note}
              className="rounded-xl border border-departure bg-departure/40 px-4 py-2 text-xs text-departure-ink"
            >
              {note}
            </p>
          ))}

        <div className="grid gap-3 lg:grid-cols-[184px_minmax(0,1fr)_336px]">
          <ScorePalette actions={actions} />

          <div
            ref={board}
            role="application"
            aria-label="Score"
            tabIndex={0}
            onKeyDown={onKeyDown}
            className="min-h-[420px] overflow-x-auto rounded-xl border border-rule bg-white p-2 outline-none focus-visible:border-ink-quiet"
          >
            {engineError !== null ? (
              <p className="p-6 text-sm text-fault-strong">
                The engraver did not load: {engineError}
              </p>
            ) : error !== null ? (
              <p className="p-6 text-sm text-fault-strong">{error}</p>
            ) : rendering === null ? (
              <p className="p-6 font-mono text-sm text-ink-quiet">Loading the engraver…</p>
            ) : (
              <ScoreView
                key={sheet.page}
                page={sheet.page}
                rendering={rendering}
                score={shown}
                caret={editor.entry.caret}
                selection={editor.selection}
                sounding={sound.playback.sounding}
                playhead={playhead.at}
                onPick={pick}
              />
            )}
          </div>

          <ScoreInspector actions={actions} chordAsk={chordAsk} figureAsk={figureAsk} />
        </div>

        <ScoreStatus
          actions={actions}
          position={sound.playback.position}
          seconds={sound.playback.schedule.seconds}
          sheet={sheet}
        />

        <input
          ref={picker}
          type="file"
          accept={SCORE_FILE_TYPES}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file === undefined) return;
            void openFile(file);
          }}
        />

        {help ? <ScoreHelp onClose={() => setHelp(false)} /> : null}
      </div>
    </Board>
  );
}
